import Stack from "@mui/material/Stack/Stack";
import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Backdrop,
  Button,
  CircularProgress,
  Fab,
  Input,
  TextField,
  Typography,
} from "@mui/material";
import { Container } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { LoadProductList } from "../../../../../../domain/usecases/load-product-list";
import { CustomDialog } from "../../../../../components/Dialog";
import { Product } from "../../../../../../domain/models/product-model";
import { StoreItem } from "../../../../../../domain/models/storage-item";
import { StoreItemCard } from "./ProductCard";
import { Total } from "../../../Components/Total";
import DrawerAppBar from "../../../../../components/mobile/Navbar";
import { useNavigate } from "react-router-dom";
import { useAuditContext } from "../../store";

type Props = {
  loadProductList: LoadProductList;
};

export const AuditProductCollector: React.FC<Props> = ({ loadProductList }) => {
  const [code, setCode] = useState<string>("");
  const [batch, setBatch] = useState<string>("");
  const [product, setProduct] = useState<Product | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [deleteItem, setDeleteItem] = useState<StoreItem | null>(null);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { list, setList } = useAuditContext();

  const navigate = useNavigate();

  const handleInputBlur = async () => {
    setIsLoading(true);
    try {
      if (code) {
        if (product && product.eanCode === code) return;
        const data = await loadProductList.load({ code });
        setProduct(data[0]);
      }
    } catch (error) {
      setIsErrorDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (item: StoreItem) => {
    setDeleteItem(item);
    setIsDeleteDialogOpen(true);
  };

  return (
    <>
      <DrawerAppBar
        title="Separar Mercadoria"
        onBack={() => {
          navigate("/mobile/check/shipments");
        }}
      />
      <Stack sx={{ m: "20px" }} spacing={4}>
        <CustomDialog
          isOpen={isErrorDialogOpen}
          title="Erro ao carregar produto"
          content={`Não foi encontrado nenhum produto com o código EAN ${code}`}
          handleClose={() => {
            setIsErrorDialogOpen(false);
          }}
        />
        <CustomDialog
          isOpen={isDeleteDialogOpen}
          title="Tem certeza que deseja excluir a confererência?"
          content={``}
          handleClose={() => {
            setIsDeleteDialogOpen(false);
          }}
          onYes={() => {
            if (!list) return;
            const newList = list.filter((item) => item !== deleteItem);
            setList(newList);
            setDeleteItem(null);
            setIsDeleteDialogOpen(false);
          }}
          onNo={() => {
            setIsDeleteDialogOpen(false);
          }}
          yesText="Confirmar"
        />
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Paper>
          <Stack spacing={4}>
            <Stack direction="row" sx={{ mx: "10px", marginTop: "30px" }}>
              <TextField
                id="outlined-basic"
                label="Código do Produto"
                variant="outlined"
                sx={{
                  mx: "5px",
                }}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                onBlur={handleInputBlur}
              />
            </Stack>
            {product?.batchControl && (
              <Container>
                <TextField
                  id="outlined-basic"
                  label="Lote do Produto"
                  variant="outlined"
                  value={batch}
                  onChange={(e) => {
                    setBatch(e.target.value);
                  }}
                  onBlur={handleInputBlur}
                />
              </Container>
            )}

            <Stack>
              <Typography sx={{ mx: "5px" }}>Quantidade</Typography>
              <Stack
                direction="row"
                sx={{
                  my: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  onClick={() => {
                    if (amount && amount !== "0") {
                      const newValue = parseFloat(amount) - 1;
                      setAmount(newValue.toString());
                    }
                  }}
                >
                  <RemoveIcon />
                </Fab>

                <Input
                  sx={{ width: "70px", mx: "30px" }}
                  type="text"
                  value={amount}
                  onChange={(event) => {
                    const inputValue = event.target.value;
                    if (/^[0-9.]+$/.test(inputValue) || inputValue === "") {
                      setAmount(inputValue);
                    }
                  }}
                />
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  onClick={() => {
                    if (amount) {
                      const newValue = parseFloat(amount) + 1;
                      setAmount(newValue.toString());
                    } else {
                      setAmount("1");
                    }
                  }}
                >
                  <AddIcon />
                </Fab>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        {product && (
          <Paper sx={{ padding: "20px" }}>
            <Stack spacing={2}>
              <Typography fontWeight="bold" fontSize="24px">
                {product.description}
              </Typography>
              <Typography>{`Fornecedor: ${product.supplier}`}</Typography>
              {batch && <Typography>{`Lote: ${batch}`}</Typography>}
              <Typography>{`Quantidade: ${amount}`}</Typography>
            </Stack>
          </Paper>
        )}

        <Button
          variant="contained"
          sx={{ height: "50px", fontWeight: "bold" }}
          disabled={
            !amount ||
            !product ||
            amount === "0" ||
            (product.batchControl && !batch)
              ? true
              : false
          }
          onClick={() => {
            setAmount("");
            setCode("");
            setBatch("");
            setProduct(null);
            setList((old) => {
              if (!old) {
                return [
                  {
                    ...product,
                    code: product?.eanCode,
                    amount: parseFloat(amount),
                    batch: batch,
                    nf: 1,
                  },
                ];
              }
              return [
                ...old,
                {
                  ...product,
                  code: product?.eanCode,
                  amount: parseFloat(amount),
                  batch: batch,
                  nf: 1,
                }!,
              ];
            });
          }}
        >
          Adicionar Produto
        </Button>
        <Button
          variant="contained"
          color="secondary"
          sx={{ color: "white", height: "50px", fontWeight: "bold" }}
          disabled={list?.length === 0}
          onClick={() => {
            navigate("/mobile/audit/list");
          }}
        >
          Finalizar Conferência
        </Button>
        <Total
          text="Total de itens inseridos"
          number={`${list?.length || 0}`}
        />

        {list?.length && list?.length > 0 && (
          <>
            {list.map((item, index) => {
              return (
                <StoreItemCard
                  item={item}
                  key={index}
                  onDelete={handleDelete}
                />
              );
            })}
          </>
        )}
      </Stack>
    </>
  );
};
